import { Injectable } from '@angular/core';
import { formatInTimeZone } from 'date-fns-tz';
import { formatIso8601DateOnly as formatIso8601DateOnlyUtil } from './local-calendar.utils';

/**
 * Timezone-aware date/time formatting (date-fns-tz).
 * For local calendar YYYY-MM-DD without IANA zones, use `local-calendar.utils.ts`.
 */
@Injectable({
  providedIn: 'root'
})
export class DateFormatService {
  /**
   * Format a date/time using the browser's local timezone
   */
  private formatInLocalTimezone(date: Date | number | string): string {
    const dateObj = typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    });
  }

  /**
   * Format a date/time for display in tooltips and charts
   * Includes timezone abbreviation (e.g., "12/30/2024, 14:30:00 EST")
   *
   * If the input is an ISO string with a timezone offset, the date will be formatted
   * preserving that timezone using date-fns-tz. Otherwise, it uses the browser's local timezone.
   *
   * @param date - Date object or timestamp (number or string)
   * @param useLocalTimezone - If true, always format in browser's local timezone (ignores ISO string timezone)
   * @returns Formatted date/time string with timezone
   */
  formatDateTimeWithTimezone(
    date: Date | number | string | null | undefined,
    useLocalTimezone: boolean = false
  ): string {
    if (!date) return '';

    // If useLocalTimezone is true, skip timezone detection and use browser's local timezone
    if (useLocalTimezone) {
      return this.formatInLocalTimezone(date);
    }

    // If input is an ISO string with timezone offset, use date-fns-tz to format it
    if (typeof date === 'string') {
      // Match ISO 8601 format with timezone offset (e.g., "2025-12-29T03:30:00-06:00" or "2025-12-29T03:30:00Z")
      const isoMatch = date.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d{3})?([+-]\d{2}:\d{2}|Z)$/);
      if (isoMatch) {
        // JavaScript's Date constructor correctly parses ISO strings with timezone offsets
        // It creates a Date object representing the correct moment in time (in UTC internally)
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          // Find the IANA timezone that matches this offset at this date
          const timeZone = this.findTimeZoneForOffset(date, dateObj);

          if (timeZone) {
            try {
              // formatInTimeZone takes a Date object (which is internally UTC) and formats it
              // in the specified timezone. Since dateObj was created from an ISO string with offset,
              // it already represents the correct moment in time. We just need to format it
              // in the matching timezone to display the original time with the correct timezone abbreviation.
              return formatInTimeZone(
                dateObj,
                timeZone,
                'M/d/yyyy, HH:mm:ss zzz'
              );
            } catch (error) {
              // Fallback if formatting fails
              console.warn(`Error formatting with timezone "${timeZone}"`, error);
            }
          }
        }
      }
    }

    // For Date objects, numbers, or strings without timezone offset, use browser's local timezone
    return this.formatInLocalTimezone(date);
  }

  /**
   * Find the IANA timezone that matches the offset in the ISO string
   * This handles DST correctly by checking which timezone has the offset at the local time
   * represented by the ISO string (not the UTC time)
   */
  private findTimeZoneForOffset(isoString: string, date: Date): string | null {
    // Extract offset from ISO string
    const offsetMatch = isoString.match(/([+-]\d{2}:\d{2}|Z)$/);
    if (!offsetMatch) return null;

    const offset = offsetMatch[1];
    if (offset === 'Z') return 'UTC';

    // Parse offset to minutes
    const offsetParts = offset.match(/([+-])(\d{2}):(\d{2})/);
    if (!offsetParts) return null;

    const [, sign, hoursStr, minutesStr] = offsetParts;
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    const targetOffsetMinutes = (sign === '-' ? -1 : 1) * (hours * 60 + minutes);

    // Representative IANA timezones - one for each offset pattern
    // We check which timezone has the matching offset at the local time (not UTC)
    // This correctly handles DST. If multiple timezones match, we return the first one.
    const timezones = [
      // US timezones
      'America/New_York',      // EST/EDT: -300/-240
      'America/Chicago',       // CST/CDT: -360/-300
      'America/Denver',        // MST/MDT: -420/-360
      'America/Los_Angeles',   // PST/PDT: -480/-420
      'America/Phoenix',       // MST (no DST): -420
      'America/Anchorage',     // AKST/AKDT: -540/-480
      'Pacific/Honolulu',      // HST (no DST): -600

      // Europe
      'Europe/London',         // GMT/BST: 0/+60
      'Europe/Berlin',         // CET/CEST: +60/+120
      'Europe/Helsinki',       // EET/EEST: +120/+180
      'Europe/Moscow',         // MSK: +180

      // Asia
      'Asia/Tokyo',            // JST: +540
      'Asia/Shanghai',         // CST: +480
      'Asia/Seoul',            // KST: +540
      'Asia/Dubai',            // GST: +240
      'Asia/Kolkata',          // IST: +330
      'Asia/Bangkok',          // ICT: +420

      // Australia
      'Australia/Sydney',     // AEDT/AEST: +660/+600
      'Australia/Brisbane',   // AEST: +600
      'Australia/Perth',       // AWST: +480
      'Australia/Adelaide',    // ACDT/ACST: +630/+570

      // South America
      'America/Sao_Paulo',     // BRT/BRST: -180/-120
      'America/Buenos_Aires',  // ART: -180
      'America/Santiago',      // CLT/CLST: -240/-180
      'America/Lima',          // PET: -300

      // Other
      'Africa/Johannesburg',    // SAST: +120
      'Africa/Cairo',          // EET/EEST: +120/+180
      'Asia/Jerusalem',        // IST/IDT: +120/+180
      'Asia/Riyadh',           // AST: +180
    ];

    // Check which timezone has this offset
    // We use the date parameter (which is the UTC representation) to check offsets
    // This works because we're checking what offset each timezone has at that UTC moment
    for (const tz of timezones) {
      try {
        // Get the offset for this timezone at this UTC date using Intl
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          timeZoneName: 'longOffset'
        });
        const parts = formatter.formatToParts(date);
        const tzOffsetStr = parts.find(p => p.type === 'timeZoneName')?.value;

        if (tzOffsetStr) {
          // Parse offset (e.g., "GMT-06:00" or "GMT-6")
          const tzOffsetMatch = tzOffsetStr.match(/GMT([+-])(\d{1,2}):?(\d{2})?/);
          if (tzOffsetMatch) {
            const [, tzSign, tzHoursStr, tzMinutesStr] = tzOffsetMatch;
            const tzHours = parseInt(tzHoursStr, 10);
            const tzMinutes = parseInt(tzMinutesStr || '0', 10);
            const tzOffsetMinutes = (tzSign === '-' ? -1 : 1) * (tzHours * 60 + tzMinutes);

            if (tzOffsetMinutes === targetOffsetMinutes) {
              return tz;
            }
          }
        }
      } catch (error) {
        // Continue to next timezone if this one fails
        continue;
      }
    }

    // If no match found, return null to fall back to browser timezone
    return null;
  }

  /**
   * Format a datetime value for display in local timezone.
   * Convenience method that always uses local timezone.
   *
   * @param date - Date object, timestamp, or ISO string
   * @returns Formatted date/time string, or '—' if null/undefined
   */
  formatDateTimeLocal(date: Date | number | string | null | undefined): string {
    if (!date) return '—';
    // Get browser's local timezone
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.formatDateTimeInTimezone(date, localTimezone);
  }

  /**
   * Format duration in seconds to a human-readable string.
   * Examples: "5.2s", "3.5m", "1.2h"
   *
   * @param seconds - Duration in seconds
   * @returns Formatted duration string, or '—' if null/undefined
   */
  formatDuration(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined) return '—';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  }

  /**
   * Format a date/time for display in tooltips (no seconds)
   * Includes timezone abbreviation (e.g., "12/30/2024, 14:30 EST")
   *
   * @param date - Date object, timestamp, or ISO string
   * @param timezone - IANA timezone name
   * @returns Formatted date/time string
   */
  formatDateTimeTooltip(
    date: Date | number | string | null | undefined,
    timezone: string
  ): string {
    return this.formatInTimezone(date, timezone, 'M/d/yyyy, HH:mm zzz');
  }

  /**
   * Format only the time and timezone abbreviation (no date)
   *
   * @param date - Date object, timestamp, or ISO string
   * @param timezone - IANA timezone name
   * @returns Formatted time string (e.g., "14:30 EST")
   */
  formatTimeWithTimezone(
    date: Date | number | string | null | undefined,
    timezone: string
  ): string {
    return this.formatInTimezone(date, timezone, 'HH:mm zzz');
  }

  /**
   * Format a date/time in a specific timezone.
   *
   * @param date - Date object, timestamp, or ISO string
   * @param timezone - IANA timezone name (e.g., 'America/New_York', 'UTC')
   * @returns Formatted date/time string with timezone abbreviation
   */
  formatDateTimeInTimezone(
    date: Date | number | string | null | undefined,
    timezone: string
  ): string {
    if (!date) return '';

    const dateObj = typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

    if (isNaN(dateObj.getTime())) return '';

    try {
      return formatInTimeZone(
        dateObj,
        timezone,
        'M/d/yyyy, HH:mm:ss zzz'
      );
    } catch (error) {
      console.warn(`Error formatting with timezone "${timezone}"`, error);
      // Fallback to local timezone
      return this.formatInLocalTimezone(dateObj);
    }
  }

  /**
   * Format a date/time in a specific timezone with a custom format pattern.
   * Uses date-fns format tokens (e.g., 'HH:mm', 'MM/dd yyyy', etc.)
   *
   * @param date - Date object, timestamp, or ISO string
   * @param timezone - IANA timezone name (e.g., 'America/New_York', 'UTC')
   * @param formatPattern - date-fns format pattern (e.g., 'HH:mm', 'MM/dd yyyy')
   * @returns Formatted date/time string
   */
  formatInTimezone(
    date: Date | number | string | null | undefined,
    timezone: string,
    formatPattern: string
  ): string {
    if (!date) return '';

    const dateObj = typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

    if (isNaN(dateObj.getTime())) return '';

    try {
      return formatInTimeZone(dateObj, timezone, formatPattern);
    } catch (error) {
      console.warn(`Error formatting with timezone "${timezone}" and pattern "${formatPattern}"`, error);
      return '';
    }
  }

  /**
   * Format a date-only string (YYYY-MM-DD) in a specific timezone.
   * The string is treated as a calendar date; the displayed date will match that date
   * in the given timezone. Uses noon UTC for that day so the calendar day is stable
   * in any timezone (avoids fromZonedTime and browser-local midnight pitfalls).
   *
   * @param dateString - Date string in YYYY-MM-DD format
   * @param timezone - IANA timezone name (e.g., 'America/New_York', 'UTC')
   * @param formatPattern - date-fns format pattern (e.g., 'MMM d, yyyy')
   * @returns Formatted date string
   */
  formatDateOnlyInTimezone(
    dateString: string | null | undefined,
    timezone: string,
    formatPattern: string = 'MMM d, yyyy'
  ): string {
    if (!dateString) return '';

    // Parse YYYY-MM-DD manually to avoid UTC parsing (new Date("2026-01-17") parses as UTC)
    const parts = dateString.split('-');
    if (parts.length !== 3) return '';

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    if (isNaN(year) || isNaN(month) || isNaN(day)) return '';

    // Noon UTC on this calendar day: same day in all timezones, no fromZonedTime/browser-TZ dependency
    const noonUtc = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

    return this.formatInTimezone(noonUtc, timezone, formatPattern);
  }

  /**
   * Convert UTC hour and minute to local time string.
   * Used for displaying cron schedule times in local timezone.
   *
   * @param utcHour - Hour in UTC (0-23)
   * @param utcMinute - Minute in UTC (0-59)
   * @returns Local time string (e.g., "4:45 AM EST")
   */
  /**
   * ISO-8601 timestamp → ``YYYY-MM-DD`` (date portion only; no clock time or TZ shift).
   * For human-friendly calendar labels use {@link formatYyyyMmDdHumanFriendly} in `local-calendar.utils`.
   */
  formatIso8601DateOnly(iso: string | null | undefined): string {
    return formatIso8601DateOnlyUtil(iso);
  }

  convertUtcToLocalTime(utcHour: number, utcMinute: number): string {
    const now = new Date();
    const utcDate = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        utcHour,
        utcMinute,
        0
      )
    );

    return utcDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
  }
}
